import * as THREE from "three";
import { Player } from "../models/Player";
import {
  PlayerState,
  Team,
  PlayerSchema,
} from "@server/rooms/schema/BasketballRoomState";
import { NetworkManager } from "./NetworkManager";
import { eventBus } from "../events/EventBus";
import { CourtManager } from "./CourtManager";
import { MapSchema } from "@colyseus/schema";
import { Debug, LogCategory } from "../../../shared/Debug";

/**
 * Simple spatial grid for fast proximity queries
 * Divides the court into a grid of cells for quick neighbor finding
 */
class SpatialGrid {
  private grid: Map<string, Player[]> = new Map();
  private cellSize: number;

  constructor(cellSize: number = 5) {
    // Cell size in game units
    this.cellSize = cellSize;
  }

  /**
   * Convert a world position to a grid cell key
   */
  private getGridKey(position: THREE.Vector3): string {
    const cellX = Math.floor(position.x / this.cellSize);
    const cellZ = Math.floor(position.z / this.cellSize);
    return `${cellX},${cellZ}`;
  }

  /**
   * Add a player to the grid
   */
  public addPlayer(player: Player): void {
    const position = player.getPosition();
    const key = this.getGridKey(position);

    if (!this.grid.has(key)) {
      this.grid.set(key, []);
    }

    // Add player to the appropriate grid cell
    this.grid.get(key)!.push(player);
  }

  /**
   * Update the grid with all players' current positions
   */
  public updateGrid(players: Map<string, Player>): void {
    // Clear the grid
    this.grid.clear();

    // Add all players to their current grid cells
    players.forEach((player) => {
      this.addPlayer(player);
    });
  }

  /**
   * Find nearby players within a radius
   */
  public getNearbyPlayers(
    position: THREE.Vector3,
    radius: number = 10,
  ): Player[] {
    const result: Player[] = [];
    const searchRadius = Math.ceil(radius / this.cellSize);

    // Get the central cell
    const centerX = Math.floor(position.x / this.cellSize);
    const centerZ = Math.floor(position.z / this.cellSize);

    // Check cells within the search radius
    for (let x = centerX - searchRadius; x <= centerX + searchRadius; x++) {
      for (let z = centerZ - searchRadius; z <= centerZ + searchRadius; z++) {
        const key = `${x},${z}`;
        const cellPlayers = this.grid.get(key);

        if (cellPlayers) {
          // Add all players from this cell
          result.push(...cellPlayers);
        }
      }
    }

    return result;
  }

  /**
   * Find the closest player to a position
   */
  public getClosestPlayer(
    position: THREE.Vector3,
    excludeIds: Set<string> = new Set(),
    team: Team | null = null,
  ): Player | null {
    // Start with small radius and expand outward for efficiency
    let radius = 5;
    const maxRadius = 50; // Maximum search radius

    while (radius <= maxRadius) {
      const nearbyPlayers = this.getNearbyPlayers(position, radius);

      // Filter by team if needed
      const candidates = nearbyPlayers.filter((player) => {
        if (excludeIds.has(player.getId())) return false;
        if (team !== null && player.getTeam() !== team) return false;
        return true;
      });

      if (candidates.length > 0) {
        // Find the closest from candidates
        let closest = candidates[0];
        let closestDist = closest.getPosition().distanceToSquared(position);

        for (let i = 1; i < candidates.length; i++) {
          const dist = candidates[i].getPosition().distanceToSquared(position);
          if (dist < closestDist) {
            closest = candidates[i];
            closestDist = dist;
          }
        }

        return closest;
      }

      // Expand search radius
      radius *= 2;
    }

    return null;
  }
}

/**
 * Manages player creation, updates, and interactions
 */
export class PlayerManager {
  private scene: THREE.Scene;
  private networkManager: NetworkManager;
  private players: Map<string, Player> = new Map();
  private localPlayer: Player | null = null;
  private courtManager: CourtManager;

  // Spatial partitioning for efficient proximity queries
  private spatialGrid = new SpatialGrid(5); // 5 unit cells
  constructor(
    scene: THREE.Scene,
    networkManager: NetworkManager,
    courtManager: CourtManager,
  ) {
    this.scene = scene;
    this.networkManager = networkManager;
    this.courtManager = courtManager;

    // Set up event listeners
    this.initializeEventListeners();
  }

  private initializeEventListeners(): void {
    // Listen for player movement events
    eventBus.subscribe(
      "player:move",
      (data: { velocityX: number; velocityZ: number }) => {
        this.movePlayer(data.velocityX, data.velocityZ);
      },
    );

    // Listen for player interaction events
    eventBus.subscribe(
      "player:shoot",
      (data: { targetBasket: THREE.Vector3 }) => {
        this.processPlayerShoot(data.targetBasket);
      },
    );

    eventBus.subscribe("player:block", () => {
      this.processPlayerBlock();
    });

    // Handle player pass event
    eventBus.subscribe('player:pass', (data: { targetX: number, targetZ: number }) => {
      this.processPlayerPass(data.targetX, data.targetZ);
    });
    
    // Handle ball animation changes
    eventBus.subscribe('ball:animation-changed', (data: { animation: string }) => {
      Debug.info('Ball animation changed to ' + data.animation, LogCategory.BALL);
    });
  }

  /**
   * Register callbacks with the network manager
   */
  public registerNetworkCallbacks(): void {
    // Add callback for when a player joins the game
    this.networkManager.onPlayerAdded((player: PlayerSchema, key) => {
      Debug.info(`Player added with ID: ${key}`, LogCategory.PLAYER);
      this.addPlayer(player, key);
    });

    // Add callback for when a player leaves the game
    this.networkManager.onPlayerRemoved((_, key) => {
      Debug.info(`Player removed with ID: ${key}`, LogCategory.PLAYER);
      this.removePlayer(key);
    });
  }

  /**
   * Add a new player to the game
   */
  public async addPlayer(playerData: PlayerSchema, id: string): Promise<void> {
    const isLocalPlayer = id === this.networkManager.getSessionId();

    let modelPath = null;
    let modelAnimationFiles = [];

    if (playerData.avatarIndex === 0) {
      modelPath = "/assets/models/mj/tpose.glb";
    } else if (playerData.avatarIndex === 1) {
      modelPath = "/assets/models/lb/tpose.glb";
    } else if (playerData.avatarIndex === 2) {
      modelPath = "/assets/models/lj/tpose.glb";
    } else if (playerData.avatarIndex === 3) {
      modelPath = "/assets/models/sc/tpose.glb";
    } else if (playerData.avatarIndex === 4) {
      modelPath = "/assets/models/pl/tpose.glb";
    } else if (playerData.avatarIndex === 5) {
      modelPath = "/assets/models/ak/tpose.glb";
    } else {
      modelPath = "/assets/models/mj/tpose.glb";
    }
    modelAnimationFiles = [
      "/assets/models/mj/idle.glb",
      "/assets/models/mj/run_dribble.glb",
    ];

    //modelPath = '/assets/models/t1/unitcube.glb';
    //modelAnimationFiles = [];

    const player = new Player(
      playerData.username,
      id, // Pass the player ID for spatial grid lookups
      playerData.team,
      isLocalPlayer,
      isLocalPlayer ? this.networkManager : null,
      modelPath,
      modelAnimationFiles,
    );

    // Get the mesh to ensure it's loaded
    const avatar = await player.getMesh();

    // Add player parent group to the scene
    this.scene.add(avatar);

    // Store player in the map
    this.players.set(id, player);

    this.updatePlayer(player, playerData);

    // Set as local player if this is the local player
    if (player.isLocalPlayer()) {
      this.localPlayer = player;

      this.localPlayer.onPlayerMove((player: Player) => {
        const velocity = player.getVelocity();
        //console.log('Player movement callback triggered with velocity:', velocity);

        // Send position update to server
        this.networkManager.sendMessage("setPlayerVelocity", {
          velocityX: velocity.x,
          velocityZ: velocity.z,
        });
        //console.log('Sent movement message to server:', { velocityX: velocity.x, velocityZ: velocity.z });
      });

      this.localPlayer.onPlayerShot(
        (velocityX: number, velocityY: number, velocityZ: number) => {
          this.networkManager.sendMessage("shoot", {
            velocityX: velocityX,
            velocityY: velocityY,
            velocityZ: velocityZ,
          });
        },
      );

      // Publish that local player is ready
      //eventBus.publish('player:local-ready', this.localPlayer);

      // Log for debugging
      Debug.info(`Local player added with ID: ${id}`, LogCategory.PLAYER);
    } else {
      Debug.info(`Remote player added with ID: ${id}`, LogCategory.PLAYER);
    }

    // Set initial move state to prevent player starting in floor
    if (isLocalPlayer) {
      setTimeout(() => {
        player.move(0.1, 0.1);
      }, 100);
    }
  }

  /**
   * Remove a player from the game and clean up all associated resources
   */
  public async removePlayer(id: string): Promise<void> {
    const player = this.players.get(id);
    if (player) {
      try {
        Debug.info(`Removing player with ID: ${id}`, LogCategory.PLAYER);

        // Get the mesh before disposing the player
        const mesh = await player.getMesh();

        // First remove from scene to stop rendering
        if (mesh) {
          this.scene.remove(mesh);
        }

        // Properly dispose of player resources
        player.dispose();

        // Remove player from the map
        this.players.delete(id);

        // Clear local player reference if this was the local player
        if (player === this.localPlayer) {
          this.localPlayer = null;
          eventBus.publish("player:local-removed");
        }

        Debug.info(
          `Player with ID: ${id} successfully removed and disposed`,
          LogCategory.PLAYER,
        );
      } catch (error) {
        Debug.error(
          `Error removing player with ID ${id}: ${error}`,
          LogCategory.PLAYER,
        );
      }
    }
  }

  public updatePlayer(player: Player, playerData: PlayerSchema): void {
    // Update position and rotation
    // Ensure y position is never below 0
    const constrainedY = Math.max(0, playerData.dynamicBody.position.y);
    player.setPosition(
      playerData.dynamicBody.position.x,
      constrainedY,
      playerData.dynamicBody.position.z,
    );
    player.setRotationFromQuarternian(
      new THREE.Quaternion(
        playerData.dynamicBody.rotation.x,
        playerData.dynamicBody.rotation.y,
        playerData.dynamicBody.rotation.z,
        playerData.dynamicBody.rotation.w,
      ),
    );

    // Set player state
    player.setState(playerData.playerState);

    // Handle ball possession changes - publish events for the BallManager
    // Only trigger an event if there's an actual change in possession
    const currentHasBall = player.hasBall();

    if (playerData.hasBall !== currentHasBall) {
      // Always update player's internal state FIRST
      player.setHasBallValue(playerData.hasBall);

      if (playerData.hasBall) {
        Debug.info(
          `Player ${playerData.id} received ball possession`,
          LogCategory.PLAYER,
        );
        eventBus.publish("ball:possession-changed", {
          playerId: playerData.id,
          hasball: true,
        });
      } else {
        Debug.info(
          `Player ${playerData.id} lost ball possession`,
          LogCategory.PLAYER,
        );
        // Ensure ball is released from player's possession
        
        eventBus.publish("ball:possession-changed", {
          playerId: playerData.id,
          hasball: false,
        });
      }
    }
  }
  /**
   * Update player positions based on server state
   */
  public updatePlayers(serverPlayers: MapSchema<PlayerSchema>): void {
    if (!serverPlayers) return;

    // Update player positions and states from server
    serverPlayers.forEach((playerData: PlayerSchema, id: string) => {
      const player = this.players.get(id);

      if (player) {
        this.updatePlayer(player, playerData);
      }
    });

    // Update all players' animations
    this.players.forEach((player) => {
      player.update();
    });

    // Update the spatial grid with new player positions
    // This ensures our spatial queries use up-to-date positions
    this.spatialGrid.updateGrid(this.players);
  }

  /**
   * Move the local player
   */
  public movePlayer(velocityX: number, velocityZ: number): void {
    //console.log('PlayerManager movePlayer called with velocities:', { velocityX, velocityZ });

    if (this.localPlayer) {
      this.localPlayer.move(velocityX, velocityZ);
      Debug.info("Movement applied to local player", LogCategory.PLAYER);
    } else {
      Debug.warn("No local player found for movement", LogCategory.PLAYER);
    }
  }

  /**
   * Process shooting for the local player
   */
  public processPlayerShoot(targetBasket: THREE.Vector3 = null): void {
    if (!this.localPlayer) return;

    if (!targetBasket) {
      // Get the target basket
      const playerPos = this.localPlayer.getPosition();
      const playerDirection = new THREE.Vector3(0, 0, 1).applyQuaternion(
        this.localPlayer.getQuaternion(),
      );

      const hoopAPosition = this.courtManager.getHoopAPosition();
      const hoopBPosition = this.courtManager.getHoopBPosition();

      // Calculate vectors to each basket
      const vectorToHoopA = new THREE.Vector3().subVectors(
        hoopAPosition,
        playerPos,
      );
      const vectorToHoopB = new THREE.Vector3().subVectors(
        hoopBPosition,
        playerPos,
      );

      // Calculate dot products to determine which basket is more in the direction the player is facing
      const dotProductA = playerDirection.dot(vectorToHoopA.normalize());
      const dotProductB = playerDirection.dot(vectorToHoopB.normalize());

      // Choose the basket with the higher dot product (more aligned with player's direction)
      targetBasket = dotProductA > dotProductB ? hoopAPosition : hoopBPosition;
    }

    // Only proceed if player has the ball
    if (this.localPlayer?.hasBall()) {
      Debug.info("Starting shooting process...", LogCategory.PLAYER);

      // Set shooting animation with a callback to release the ball
      const releasePosition = this.localPlayer.shoot(targetBasket);

      // Set target rotation to the direction of the target basket
      const playerPos = this.localPlayer.getPosition();
      const targetLookAtPoint = new THREE.Vector3(
        targetBasket.x,
        playerPos.y,
        targetBasket.z,
      ); // Look at basket's XZ but at player's height

      // Ensure the player doesn't try to look straight up/down if target is directly above/below
      if (
        Math.abs(playerPos.x - targetLookAtPoint.x) < 0.01 &&
        Math.abs(playerPos.z - targetLookAtPoint.z) < 0.01
      ) {
        // Player is directly above/below target, keep current rotation or default
        targetLookAtPoint.x += 0.01; // Add small offset to avoid issues
      }

      // Create a temporary matrix
      const lookAtMatrix = new THREE.Matrix4();

      // Swap playerPos and targetLookAtPoint
      // Set the matrix to represent the rotation needed to look FROM the target TOWARDS the player
      lookAtMatrix.lookAt(
        targetLookAtPoint,
        playerPos,
        new THREE.Vector3(0, 1, 0),
      ); // Use standard up vector

      // Extract the quaternion from the matrix
      const targetRotation = new THREE.Quaternion().setFromRotationMatrix(
        lookAtMatrix,
      );

      // Send the calculated rotation to the server
      this.networkManager.sendMessage("setPlayerTargetRotation", {
        rotationX: targetRotation.x,
        rotationY: targetRotation.y,
        rotationZ: targetRotation.z,
        rotationW: targetRotation.w,
      });

      // tell the server
      this.networkManager.sendMessage("setPlayerState", {
        playerState: PlayerState.SHOOTING,
      });

      // Publish event for ball manager
      //eventBus.publish('ball:shot', { releasePosition, targetBasket });
    }
  }

  /**
   * Process blocking for the local player
   */
  public processPlayerBlock(): void {
    if (!this.localPlayer) return;

    this.localPlayer.block();

    // tell the server
    this.networkManager.sendMessage("setPlayerState", {
      playerState: PlayerState.BLOCKING,
    });
  }

  /**
   * Process passing for the local player
   */
  public processPlayerPass(targetX: number, targetZ: number): void {
    if (!this.localPlayer?.hasBall()) return;

    const playerPos = this.localPlayer.getPosition();
    const targetPos = new THREE.Vector3(targetX, playerPos.y, targetZ);

    // Calculate distance to target
    const distanceToTarget = playerPos.distanceTo(targetPos);

    // Calculate direction to target
    const direction = new THREE.Vector3()
      .subVectors(targetPos, playerPos)
      .normalize();

    // Enhanced pass velocity calculation
    // Shorter passes should be gentler and more controlled
    // Longer passes should be stronger and have more arc
    const MIN_PASS_SPEED = 6;  // Reduced from 8 for gentler short passes
    const MAX_PASS_SPEED = 18; // Increased from 15 for stronger long passes
    const DISTANCE_THRESHOLD = 15; // Distance at which to start increasing arc
    
    // Non-linear speed scaling based on distance
    // Use quadratic scaling for more natural feel
    const distanceFactor = Math.min(1, (distanceToTarget / DISTANCE_THRESHOLD) ** 0.5);
    const passSpeed = MIN_PASS_SPEED + (MAX_PASS_SPEED - MIN_PASS_SPEED) * distanceFactor;

    // Dynamic vertical component based on distance
    // Short passes should be flatter, long passes should have more arc
    const baseVerticalComponent = 1.0; // Base upward velocity for short passes
    const maxVerticalBoost = 3.0;      // Maximum additional upward velocity for long passes
    const verticalComponent = baseVerticalComponent + 
                            (maxVerticalBoost * Math.min(1, distanceToTarget / DISTANCE_THRESHOLD));

    // Calculate final velocity components
    const velocityX = direction.x * passSpeed;
    const velocityY = verticalComponent;
    const velocityZ = direction.z * passSpeed;

    // Start pass animation
    this.localPlayer.pass(targetPos);

    // Send pass message to server after a short delay to match animation
    setTimeout(() => {
      this.networkManager.sendMessage("pass", {
        velocityX,
        velocityY,
        velocityZ
      });
    }, 200); // Time this with the pass animation
  }

  /**
   * Get the local player
   */
  public getLocalPlayer(): Player | null {
    return this.localPlayer;
  }

  /**
   * Get the player position
   */
  public getPlayerPosition(): THREE.Vector3 | null {
    return this.localPlayer ? this.localPlayer.getPosition() : null;
  }

  /**
   * Get the player rotation
   */
  public getPlayerRotation(): THREE.Euler | null {
    return this.localPlayer ? this.localPlayer.getRotation() : null;
  }

  /**
   * Check if the local player has the ball
   */
  public playerHasBall(): boolean {
    return this.localPlayer ? this.localPlayer.hasBall() : false;
  }

  /**
   * Get the closest teammate position with optimized spatial lookup
   */
  public getClosestTeammatePosition(
    position: THREE.Vector3,
  ): THREE.Vector3 | null {
    if (!this.localPlayer) return null;

    // Get the local player team
    const myTeam = this.localPlayer.getTeam();

    // Create a set of IDs to exclude (just local player)
    const excludeIds = new Set<string>([this.localPlayer.getId()]);

    // Use the spatial grid to find the closest teammate efficiently
    // This is O(1) for most cases, worst case O(log n) instead of O(n)
    const closestTeammate = this.spatialGrid.getClosestPlayer(
      position,
      excludeIds,
      myTeam,
    );

    // Return the position if found, otherwise null
    return closestTeammate ? closestTeammate.getPosition() : null;
  }

  /**
   * Give ball possession to the local player
   */
  public pickupBall(): void {
    if (!this.localPlayer) return;

    // Send message to server
    this.networkManager.sendMessage("pickup_ball", {});

    // Publish event for ball manager
    eventBus.publish("ball:pickup-requested", {
      playerId: this.networkManager.getSessionId(),
    });
  }

  /**
   * Release the ball from player's possession
   */
  public releaseBall(velocity?: THREE.Vector3): void {
    if (!this.localPlayer) return;

    // Update local state first
    this.localPlayer.setHasBallValue(false);
    
    // Send message to server
    this.networkManager.sendMessage("release_ball", {
      velocityX: velocity?.x || 0,
      velocityY: velocity?.y || 0,
      velocityZ: velocity?.z || 0,
    });

    // Publish event for ball manager
    eventBus.publish("ball:release-requested", { velocity });
    eventBus.publish("ball:possession-changed", {
      playerId: this.networkManager.getSessionId(),
      hasball: false,
    });
  }

  /**
   * Returns the ball to the player's hand if no other player has it
   */
  public returnBallToPlayer(): void {
    if (!this.localPlayer) return;

    // Send message to server
    this.networkManager.sendMessage("request_ball", {});

    // Publish event for ball manager
    eventBus.publish("ball:return-requested", {
      playerId: this.networkManager.getSessionId(),
    });
  }

  /**
   * Set ball possession for a player
   */
  public setPlayerHasBall(playerId: string, hasBall: boolean, ball: any): void {
    const player = this.players.get(playerId);
    if (player) {
      try {
        Debug.info(
          `PlayerManager.setPlayerHasBall: Player ${playerId} (${player.getName()}) ${hasBall ? "receiving" : "losing"} the ball`,
          LogCategory.PLAYER,
        );

        // First update the hasBallValue to prevent recursive event triggering
        player.setHasBallValue(hasBall);

        // Then update the visual representation if a ball is provided
        if (ball) {
          // Getting player position for debugging
          const playerPos = player.getPosition();
          Debug.info(
            `Player position before ball attachment: x=${playerPos.x.toFixed(2)}, y=${playerPos.y.toFixed(2)}, z=${playerPos.z.toFixed(2)}`,
            LogCategory.PLAYER,
          );

          // Get ball position for debugging
          const ballMesh = ball.getMesh();
          const ballWorldPos = new THREE.Vector3();
          if (ballMesh && ballMesh.parent) {
            ballMesh.getWorldPosition(ballWorldPos);
            Debug.info(
              `Ball world position before attachment: x=${ballWorldPos.x.toFixed(2)}, y=${ballWorldPos.y.toFixed(2)}, z=${ballWorldPos.z.toFixed(2)}`,
              LogCategory.BALL,
            );
          }

          // Attempt to attach the ball
          player.setHasBall(hasBall, ball);

          // Verify attachment worked properly
          if (hasBall && ballMesh) {
            // Check if ball is properly attached
            if (ballMesh.parent === player.getCharacterMesh()) {
              Debug.info(
                `Ball successfully attached to player ${playerId}'s mesh`,
                LogCategory.PLAYER,
              );
            } else {
              Debug.warn(
                `Failed to attach ball to player ${playerId}. Ball parent: ${ballMesh.parent}`,
                LogCategory.PLAYER,
              );
            }
          }
        } else {
          Debug.warn(
            `No ball object provided when setting possession for player ${playerId}`,
            LogCategory.PLAYER,
          );
        }

        // Log the possession state for debugging
        Debug.info(
          `PlayerManager.setPlayerHasBall: Player ${playerId} now ${hasBall ? "has" : "does not have"} the ball`,
          LogCategory.PLAYER,
        );
      } catch (error) {
        Debug.error(
          `Error in setPlayerHasBall for player ${playerId}: ${error}`,
          LogCategory.PLAYER,
        );
      }
    } else {
      Debug.warn(
        `PlayerManager.setPlayerHasBall: Could not find player ${playerId}`,
        LogCategory.PLAYER,
      );
    }
  }

  /**
   * Register animations with all players
   */
  public registerAnimations(): void {
    Debug.info("DEPRECATED: Registering animations with all players", LogCategory.PLAYER);
    
  }

  /**
   * Update player configurations
   */
  public updatePlayerConfigs(): void {
    Debug.info("DEPRECATED: Updating player configurations", LogCategory.PLAYER);
  }

  /**
   * Cleanup resources
   */
  public dispose(): void {
    // Remove all players
    for (const player of this.players.values()) {
      player.getMesh().then((mesh) => {
        this.scene.remove(mesh);
      });
    }

    // Clear the players map
    this.players.clear();

    // Clear local player reference
    this.localPlayer = null;
  }

  /**
   * Trigger ball dribble animation
   */
  public triggerDribbleAnimation(): void {
    if (!this.localPlayer || !this.localPlayer.hasBall()) return;

    // Send message to server to set ball animation to dribble
    this.networkManager.sendMessage("set_ball_animation", {
      animation: "dribble",
    });

    // Publish event for ball manager
    eventBus.publish("ball:animation-changed", { animation: "dribble" });
  }

  /**
   * Stop ball dribble animation
   */
  public stopDribbleAnimation(): void {
    if (!this.localPlayer) return;

    // Send message to server to set ball animation to idle
    this.networkManager.sendMessage("set_ball_animation", {
      animation: "idle",
    });

    // Publish event for ball manager
    eventBus.publish("ball:animation-changed", { animation: "idle" });
  }

  /**
   * Get all players
   */
  public getAllPlayers(): Player[] {
    return Array.from(this.players.values());
  }
}
